import { Fragment, Suspense, lazy, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CharacterCount,
  Checkboxes,
  DateInput,
  ErrorMessage,
  Fieldset,
  Hint,
  Input,
  InsetText,
  Radios,
  RequiredMarker,
  Select,
  Switch,
  WarningText,
  cx,
  type ChoiceItem,
  type DateParts,
  type ErrorSummaryItem,
} from '../../components/idsk';
import { isoToParts } from '../../components/idsk/DateInput';
import { useAnnouncer } from '../../layout/Announcer';
import { evaluate } from '../evaluate';
import { flatten } from '../merge';
import { RuleSummarizer } from '../summary';
import {
  fallsBackToSlovak,
  isLeaf,
  stateLabel,
  text,
  type EvaluationContext,
  type EvaluationResult,
  type FieldDef,
  type FieldError,
  type FormDefinition,
  type FormValues,
  type JsonValue,
} from '../types';
import { errorMessage, errorTargetId, fieldId, parsePath } from './messages';
import { displayNumber, toCanonicalNumber } from './numbers';
import { ReferenceField } from './ReferenceField';
import { isRichText } from '../richText';
import { TableInput } from './TableInput';

/** The WYSIWYG editor of formatted text is loaded only for forms that have such a field. */
const RichTextEditor = lazy(() => import('./RichTextEditor'));

export type Lang = 'sk' | 'en';

export interface FormRendererProps {
  /** Used as id prefix: {formType}-{key}[-{row}]. */
  formType: string;
  definition: FormDefinition;
  /** Current values including what the user is still typing. */
  values: FormValues;
  onChange: (values: FormValues) => void;
  context: EvaluationContext;
  /** Language of labels, hints and options (the preview can show en while the UI is sk). */
  lang: Lang;
  showErrors: boolean;
  /** Change to re-synchronize the evaluated snapshot with `values` (e.g. after loading from the server). */
  resetKey?: unknown;
  onEvaluate?: (result: EvaluationResult) => void;
}

interface RowContext {
  group: FieldDef;
  row: number;
}

type Rows = Record<string, JsonValue>[];

function rowsOf(values: FormValues, key: string): Rows {
  const v = values[key];
  return Array.isArray(v) ? (v.filter((r) => r && typeof r === 'object' && !Array.isArray(r)) as Rows) : [];
}

/**
 * Renders a dynamic status form with ID-SK components. Rules are re-evaluated immediately for choice fields and on
 * blur for text fields (never on every keystroke). Hidden fields are removed from the DOM; disabled fields stay
 * focusable with aria-disabled and a hint saying when they become available. Changes are announced politely once
 * per interaction; focus never moves by itself except after adding/removing a repeating row.
 */
export function FormRenderer({ formType, definition, values, onChange, context, lang, showErrors, resetKey, onEvaluate }: FormRendererProps) {
  const { t } = useTranslation();
  const { announce } = useAnnouncer();
  const [committed, setCommitted] = useState<FormValues>(values);
  const [syncedKey, setSyncedKey] = useState(resetKey);
  if (syncedKey !== resetKey) {
    setSyncedKey(resetKey);
    setCommitted(values);
  }
  const result = useMemo(() => evaluate(definition, committed, context), [definition, committed, context]);
  const summarizer = useMemo(() => new RuleSummarizer(definition, context), [definition, context]);
  const index = useMemo(() => new Map(flatten(definition.fields).map((f) => [f.key, f])), [definition]);
  const previous = useRef<EvaluationResult>(result);

  useEffect(() => {
    onEvaluate?.(result);
  }, [result, onEvaluate]);

  const errorsByPath = useMemo(() => {
    const map = new Map<string, FieldError>();
    if (showErrors) for (const e of result.errors) if (!map.has(e.field)) map.set(e.field, e);
    return map;
  }, [result, showErrors]);

  /** Commit a snapshot for evaluation and announce what appeared/disappeared/became (un)available. */
  const commit = (next: FormValues, announceDiff = true) => {
    const nextResult = evaluate(definition, next, context);
    if (announceDiff) {
      const message = describeChanges(previous.current, nextResult, index, t, lang);
      if (message) announce(message);
    }
    previous.current = nextResult;
    setCommitted(next);
  };

  const setValue = (key: string, value: JsonValue | undefined, rowCtx: RowContext | undefined, commitNow: boolean) => {
    let next: FormValues;
    if (rowCtx) {
      const rows = rowsOf(values, rowCtx.group.key).map((r, i) => (i === rowCtx.row ? withValue(r, key, value) : r));
      next = { ...values, [rowCtx.group.key]: rows };
    } else {
      next = withValue(values, key, value);
    }
    onChange(next);
    if (commitNow) commit(next);
  };

  const getValue = (key: string, rowCtx?: RowContext): JsonValue | undefined =>
    rowCtx ? rowsOf(values, rowCtx.group.key)[rowCtx.row]?.[key] : values[key];

  const pathOf = (key: string, rowCtx?: RowContext) => (rowCtx ? `${rowCtx.group.key}[${rowCtx.row}].${key}` : key);

  const addRow = (group: FieldDef) => {
    const rows = [...rowsOf(values, group.key), {}];
    const next = { ...values, [group.key]: rows };
    onChange(next);
    commit(next, false);
    announce(t('form.rowAdded', { n: rows.length, label: text(group.label, lang) }));
    requestAnimationFrame(() => focusFirstControl(`${formType}-${group.key}-${rows.length - 1}`));
  };

  const removeRow = (group: FieldDef, row: number) => {
    const rows = rowsOf(values, group.key).filter((_, i) => i !== row);
    const next = { ...values, [group.key]: rows };
    onChange(next);
    commit(next, false);
    announce(t('form.rowRemoved', { n: row + 1, label: text(group.label, lang) }));
    requestAnimationFrame(() => {
      if (row < rows.length) focusFirstControl(`${formType}-${group.key}-${row}`);
      else document.getElementById(`${formType}-${group.key}-add`)?.focus();
    });
  };

  const labelProps = (f: FieldDef) => ({ label: text(f.label, lang), labelLang: fallsBackToSlovak(f.label, lang) ? 'sk' : undefined });
  const hintProps = (f: FieldDef) =>
    f.hint && text(f.hint, lang) ? { hint: text(f.hint, lang), hintLang: fallsBackToSlovak(f.hint, lang) ? 'sk' : undefined } : {};

  /** Choice fields followed directly by fields shown for one of their options use the conditional-reveal markup. */
  const revealsFor = (f: FieldDef, siblings: FieldDef[], i: number, rowCtx?: RowContext) => {
    const map = new Map<string, FieldDef[]>();
    if (!['SINGLE_CHOICE', 'MULTI_CHOICE', 'YES_NO'].includes(f.type)) return { map, consumed: 0 };
    if ((f.type === 'SINGLE_CHOICE' || f.type === 'MULTI_CHOICE') && f.constraints?.display === 'select') return { map, consumed: 0 };
    const ref = rowCtx ? `row.${f.key}` : f.key;
    let consumed = 0;
    for (let j = i + 1; j < siblings.length; j++) {
      const d = siblings[j]!;
      const c = d.visibleWhen;
      const ok = c && isLeaf(c) && c.field === ref && (c.op === 'eq' || c.op === 'includes') && (typeof c.value === 'string' || typeof c.value === 'boolean');
      if (!ok || d.enabledWhen) break;
      const optionValue = String((c as { value: string | boolean }).value);
      map.set(optionValue, [...(map.get(optionValue) ?? []), d]);
      consumed++;
    }
    return { map, consumed };
  };

  const renderFields = (fields: FieldDef[], rowCtx?: RowContext, disabledReason?: string): ReactNode[] => {
    const nodes: ReactNode[] = [];
    for (let i = 0; i < fields.length; i++) {
      const f = fields[i]!;
      const { map, consumed } = revealsFor(f, fields, i, rowCtx);
      nodes.push(<Fragment key={f.key}>{renderField(f, rowCtx, disabledReason, map)}</Fragment>);
      i += consumed;
    }
    return nodes;
  };

  const renderField = (f: FieldDef, rowCtx: RowContext | undefined, inheritedReason: string | undefined, reveals: Map<string, FieldDef[]>): ReactNode => {
    const path = pathOf(f.key, rowCtx);
    const state = result.state[path];
    if (!state || !state.visible) return null;
    const id = fieldId(formType, f.key, rowCtx?.row);
    const disabled = !state.enabled;
    const ownReason = f.enabledWhen ? (summarizer.summaryOf(f, 'enabledWhen', lang) ?? undefined) : undefined;
    // the hint says when the field becomes available: the parent's rule when the parent is disabled, else its own
    const reason = disabled ? (inheritedReason ?? ownReason) : undefined;
    const disabledHintId = `${id}-disabled`;
    const disabledHint = disabled && reason ? (
      <span id={disabledHintId} className="app-disabled-hint">
        {reason}
      </span>
    ) : null;
    const error = errorsByPath.get(path);
    const errorText = error ? errorMessage(t, f, error, lang, summarizer) : undefined;
    const required = state.required;
    const common = {
      formGroupClassName: cx(disabled && 'app-field--disabled'),
      extraDescribedBy: disabledHint ? disabledHintId : undefined,
    };
    const value = getValue(f.key, rowCtx);
    // options with a rule are offered only while it holds (the engine says which)
    const offeredOptions = state.options ? (f.options ?? []).filter((o) => state.options!.includes(o.value)) : (f.options ?? []);
    const revealContent = (optionValue: string): ReactNode => {
      const deps = reveals.get(optionValue);
      if (!deps) return undefined;
      const rendered = deps.map((d) => <Fragment key={d.key}>{renderField(d, rowCtx, undefined, new Map())}</Fragment>);
      return rendered;
    };

    switch (f.type) {
      case 'INFO': {
        const body = text(f.label, lang);
        const bodyLang = fallsBackToSlovak(f.label, lang) ? 'sk' : undefined;
        return f.constraints?.variant === 'warning' ? (
          <WarningText lang={bodyLang}>{body}</WarningText>
        ) : (
          <InsetText id={id} lang={bodyLang}>
            {body}
          </InsetText>
        );
      }
      case 'SECTION': {
        const sectionReason = disabled ? (ownReason ?? inheritedReason) : undefined;
        return (
          <Fieldset
            id={id}
            className={cx(disabled && 'app-field--disabled')}
            legend={<h2 className="govuk-fieldset__heading">{text(f.label, lang)}</h2>}
            legendClassName="govuk-fieldset__legend--m"
            legendLang={fallsBackToSlovak(f.label, lang) ? 'sk' : undefined}
            aria-describedby={cx(f.hint ? `${id}-hint` : undefined, disabledHint ? disabledHintId : undefined) || undefined}
          >
            {f.hint && text(f.hint, lang) && (
              <Hint id={`${id}-hint`} lang={fallsBackToSlovak(f.hint, lang) ? 'sk' : undefined}>
                {text(f.hint, lang)}
              </Hint>
            )}
            {disabledHint}
            {renderFields(f.children ?? [], rowCtx, sectionReason)}
          </Fieldset>
        );
      }
      case 'REPEATING_GROUP': {
        const rows = rowsOf(values, f.key);
        const max = f.constraints?.maxRows;
        const groupError = errorText;
        return (
          <div className={cx('govuk-form-group', groupError && 'govuk-form-group--error', disabled && 'app-field--disabled')} id={id}>
            <h2 className="govuk-heading-m" lang={fallsBackToSlovak(f.label, lang) ? 'sk' : undefined}>
              {text(f.label, lang)}
              <RequiredMarker required={required} />
            </h2>
            {f.hint && text(f.hint, lang) && (
              <Hint id={`${id}-hint`} lang={fallsBackToSlovak(f.hint, lang) ? 'sk' : undefined}>
                {text(f.hint, lang)}
              </Hint>
            )}
            {groupError && <ErrorMessage id={`${id}-error`}>{groupError}</ErrorMessage>}
            {disabledHint}
            {rows.length === 0 && <p className="govuk-body">{t('form.noRows')}</p>}
            {rows.map((_, rowIndex) => (
              <fieldset className="govuk-fieldset app-repeating-row" id={`${formType}-${f.key}-${rowIndex}`} key={rowIndex}>
                <legend className="govuk-fieldset__legend govuk-fieldset__legend--s">{t('form.rowLegend', { label: text(f.label, lang), n: rowIndex + 1 })}</legend>
                {renderFields(f.children ?? [], { group: f, row: rowIndex }, disabled ? reason : undefined)}
                <Button
                  variant="secondary"
                  className="govuk-!-margin-bottom-0"
                  aria-disabled={disabled ? true : undefined}
                  onClick={() => {
                    if (!disabled) removeRow(f, rowIndex);
                  }}
                >
                  {t('form.removeRow', { n: rowIndex + 1 })}
                </Button>
              </fieldset>
            ))}
            {max !== undefined && rows.length >= max ? (
              <p className="govuk-body">{t('form.maxRowsReached', { count: max })}</p>
            ) : (
              <Button
                id={`${id}-add`}
                variant="secondary"
                aria-disabled={disabled ? true : undefined}
                aria-describedby={cx(groupError ? `${id}-error` : undefined, f.hint ? `${id}-hint` : undefined) || undefined}
                onClick={() => {
                  if (!disabled) addRow(f);
                }}
              >
                {t('form.addRow')}
                <span className="govuk-visually-hidden">{`: ${text(f.label, lang)}`}</span>
              </Button>
            )}
          </div>
        );
      }
      case 'TEXT': {
        const format = f.constraints?.format ?? 'none';
        return (
          <Input
            id={id}
            name={path}
            {...labelProps(f)}
            labelClassName="govuk-label--s"
            {...hintProps(f)}
            error={errorText}
            required={required}
            width={(f.width as never) ?? 'two-thirds'}
            type={format === 'email' ? 'email' : format === 'phone' ? 'tel' : 'text'}
            inputMode={format === 'ico' || format === 'psc' ? 'numeric' : undefined}
            spellCheck={format !== 'none' ? false : undefined}
            autoComplete="off"
            value={typeof value === 'string' ? value : ''}
            readOnly={disabled}
            aria-disabled={disabled ? true : undefined}
            beforeInput={disabledHint}
            onChange={(e) => setValue(f.key, e.target.value, rowCtx, false)}
            onBlur={() => commit(values)}
            {...common}
          />
        );
      }
      case 'RICH_TEXT':
        return (
          <Suspense fallback={<p className="govuk-body">{t('common.loading')}</p>}>
            <RichTextEditor
              id={id}
              {...labelProps(f)}
              {...hintProps(f)}
              error={errorText}
              required={required}
              disabled={disabled}
              beforeInput={disabledHint}
              maxLength={f.constraints?.maxLength ?? 8000}
              value={isRichText(value) ? value : null}
              onChange={(v) => setValue(f.key, v ? (v as unknown as JsonValue) : undefined, rowCtx, false)}
              onBlur={() => commit(values)}
              {...common}
            />
          </Suspense>
        );
      case 'TABLE':
        return (
          <TableInput
            id={id}
            field={f}
            lang={lang}
            error={error}
            errorText={errorText}
            required={required}
            disabled={disabled}
            disabledHint={disabledHint}
            value={value}
            formGroupClassName={common.formGroupClassName}
            extraDescribedBy={common.extraDescribedBy}
            onDraft={(v) => setValue(f.key, v, rowCtx, false)}
            onCommit={() => commit(values)}
          />
        );
      case 'TEXTAREA':
        return (
          <CharacterCount
            id={id}
            name={path}
            {...labelProps(f)}
            labelClassName="govuk-label--s"
            {...hintProps(f)}
            error={errorText}
            required={required}
            maxLength={f.constraints?.maxLength ?? 8000}
            rows={f.constraints?.rows ?? 5}
            value={typeof value === 'string' ? value : ''}
            readOnly={disabled}
            aria-disabled={disabled ? true : undefined}
            beforeInput={disabledHint}
            onChange={(e) => setValue(f.key, e.target.value, rowCtx, false)}
            onBlur={() => commit(values)}
            {...common}
          />
        );
      case 'NUMBER':
        return (
          <NumberInput
            id={id}
            name={path}
            field={f}
            lang={lang}
            labelProps={labelProps(f)}
            hintProps={hintProps(f)}
            error={errorText}
            required={required}
            disabled={disabled}
            disabledHint={disabledHint}
            value={value}
            common={common}
            onCommit={(canonical) => setValue(f.key, canonical === '' ? undefined : canonical, rowCtx, true)}
          />
        );
      case 'DATE':
        return (
          <DateField
            id={id}
            field={f}
            lang={lang}
            label={text(f.label, lang)}
            labelLang={fallsBackToSlovak(f.label, lang) ? 'sk' : undefined}
            hintProps={hintProps(f)}
            error={errorText}
            required={required}
            disabled={disabled}
            disabledHint={disabledHint}
            value={value}
            common={common}
            onDraft={(iso) => setValue(f.key, iso, rowCtx, false)}
            onCommit={(iso) => setValue(f.key, iso, rowCtx, true)}
          />
        );
      case 'SINGLE_CHOICE': {
        const items: ChoiceItem[] = offeredOptions.map((o) => ({
          value: o.value,
          label: text(o.label, lang),
          lang: fallsBackToSlovak(o.label, lang) ? 'sk' : undefined,
          conditional: revealContent(o.value),
        }));
        if (f.constraints?.display === 'select') {
          return (
            <Select
              id={id}
              name={path}
              {...labelProps(f)}
              labelClassName="govuk-label--s"
              {...hintProps(f)}
              error={errorText}
              required={required}
              placeholder={t('common.select')}
              options={items.map((o) => ({ value: o.value, label: String(o.label), lang: o.lang }))}
              value={typeof value === 'string' ? value : ''}
              width={selectWidth(f.width)}
              aria-disabled={disabled ? true : undefined}
              beforeInput={disabledHint}
              onChange={(e) => {
                if (!disabled) setValue(f.key, e.target.value || undefined, rowCtx, true);
              }}
              {...common}
            />
          );
        }
        return (
          <Radios
            name={path}
            idPrefix={id}
            legend={text(f.label, lang)}
            legendLang={fallsBackToSlovak(f.label, lang) ? 'sk' : undefined}
            legendClassName="govuk-fieldset__legend--s"
            {...hintProps(f)}
            error={errorText}
            required={required}
            items={items}
            value={typeof value === 'string' ? value : null}
            ariaDisabled={disabled}
            beforeItems={disabledHint}
            onChange={(v) => setValue(f.key, v, rowCtx, true)}
            {...common}
          />
        );
      }
      case 'YES_NO': {
        const items: ChoiceItem[] = [
          { value: 'true', label: t('common.yes'), conditional: revealContent('true') },
          { value: 'false', label: t('common.no'), conditional: revealContent('false') },
        ];
        return (
          <Radios
            name={path}
            idPrefix={id}
            legend={text(f.label, lang)}
            legendLang={fallsBackToSlovak(f.label, lang) ? 'sk' : undefined}
            legendClassName="govuk-fieldset__legend--s"
            {...hintProps(f)}
            error={errorText}
            required={required}
            inline={!reveals.size}
            items={items}
            value={typeof value === 'boolean' ? String(value) : null}
            ariaDisabled={disabled}
            beforeItems={disabledHint}
            onChange={(v) => setValue(f.key, v === 'true', rowCtx, true)}
            {...common}
          />
        );
      }
      case 'MULTI_CHOICE': {
        const items: ChoiceItem[] = offeredOptions.map((o) => ({
          value: o.value,
          label: text(o.label, lang),
          lang: fallsBackToSlovak(o.label, lang) ? 'sk' : undefined,
          conditional: revealContent(o.value),
        }));
        const selected = Array.isArray(value) ? (value.filter((x) => typeof x === 'string') as string[]) : [];
        if (f.constraints?.display === 'select') {
          const hint = hintProps(f);
          return (
            <Select
              id={id}
              name={path}
              {...labelProps(f)}
              labelClassName="govuk-label--s"
              hint={
                <>
                  {hint.hint && (
                    <span className="app-block" lang={hint.hintLang}>
                      {hint.hint}
                    </span>
                  )}
                  {t('form.multiSelectHint')}
                </>
              }
              error={errorText}
              required={required}
              multiple
              size={f.constraints?.rows ?? Math.min(Math.max(items.length, 2), 8)}
              width={selectWidth(f.width) ?? 'full'}
              options={items.map((o) => ({ value: o.value, label: String(o.label), lang: o.lang }))}
              value={selected}
              aria-disabled={disabled ? true : undefined}
              beforeInput={disabledHint}
              onChange={(e) => {
                if (disabled) return;
                const next = Array.from(e.target.selectedOptions, (o) => o.value);
                setValue(f.key, next.length ? next : undefined, rowCtx, true);
              }}
              {...common}
            />
          );
        }
        return (
          <Checkboxes
            name={path}
            idPrefix={id}
            legend={text(f.label, lang)}
            legendLang={fallsBackToSlovak(f.label, lang) ? 'sk' : undefined}
            legendClassName="govuk-fieldset__legend--s"
            {...hintProps(f)}
            error={errorText}
            required={required}
            items={items}
            value={selected}
            ariaDisabled={disabled}
            beforeItems={disabledHint}
            onChange={(v) => setValue(f.key, v.length ? v : undefined, rowCtx, true)}
            {...common}
          />
        );
      }
      case 'TEAM_MEMBER':
        return (
          <Select
            id={id}
            name={path}
            {...labelProps(f)}
            labelClassName="govuk-label--s"
            {...hintProps(f)}
            error={errorText}
            required={required}
            placeholder={t('common.select')}
            options={(context.teamMembers ?? []).map((m) => ({ value: m.id, label: m.name }))}
            value={typeof value === 'string' ? value : ''}
            aria-disabled={disabled ? true : undefined}
            beforeInput={disabledHint}
            onChange={(e) => {
              if (!disabled) setValue(f.key, e.target.value || undefined, rowCtx, true);
            }}
            {...common}
          />
        );
      case 'SWITCH':
        return (
          <Switch
            id={id}
            name={path}
            {...labelProps(f)}
            labelClassName="govuk-label--s"
            {...hintProps(f)}
            error={errorText}
            onText={t('form.switchOn')}
            offText={t('form.switchOff')}
            checked={value === true}
            ariaDisabled={disabled}
            beforeInput={disabledHint}
            onChange={(on) => setValue(f.key, on, rowCtx, true)}
            {...common}
          />
        );
      case 'AUTOCOMPLETE': {
        const hint = hintProps(f);
        return (
          <ReferenceField
            id={id}
            source={f.constraints?.source ?? 'SUBJECT'}
            label={text(f.label, lang)}
            labelLang={fallsBackToSlovak(f.label, lang) ? 'sk' : undefined}
            hint={hint.hint}
            hintLang={hint.hintLang}
            error={errorText}
            required={required}
            disabled={disabled}
            disabledHint={disabledHint}
            value={typeof value === 'string' ? value : null}
            onChange={(ref) => setValue(f.key, ref, rowCtx, true)}
            {...common}
          />
        );
      }
      default:
        return null;
    }
  };

  return <div className="app-dynamic-form">{renderFields(definition.fields)}</div>;
}

/** Whether the form has fields that are (or may become) required: it then explains the asterisk (D25). */
export function hasRequiredFields(definition: FormDefinition): boolean {
  return flatten(definition.fields).some((f) => f.required || f.requiredWhen);
}

const SELECT_WIDTHS = ['full', 'three-quarters', 'two-thirds', 'one-half', 'one-third', 'one-quarter'] as const;

/** The width of a list (a fraction of the column; the character widths of inputs do not apply). */
function selectWidth(width: string | undefined): (typeof SELECT_WIDTHS)[number] | undefined {
  return SELECT_WIDTHS.find((w) => w === width);
}

function withValue<T extends Record<string, JsonValue>>(obj: T, key: string, value: JsonValue | undefined): T {
  const next = { ...obj } as Record<string, JsonValue>;
  if (value === undefined) delete next[key];
  else next[key] = value;
  return next as T;
}

function focusFirstControl(containerId: string) {
  const container = document.getElementById(containerId);
  container?.querySelector<HTMLElement>('input, select, textarea')?.focus();
}

/** One polite message per interaction: newly shown/hidden fields and fields that became (un)available. */
export function describeChanges(
  before: EvaluationResult,
  after: EvaluationResult,
  index: Map<string, FieldDef>,
  t: (key: string, options?: Record<string, unknown>) => string,
  lang: Lang,
): string {
  const paths = new Set([...Object.keys(before.state), ...Object.keys(after.state)]);
  let shown = 0;
  let hidden = 0;
  const enabled: string[] = [];
  const disabled: string[] = [];
  for (const path of paths) {
    const field = index.get(parsePath(path).key);
    if (!field || field.type === 'SECTION') continue;
    const a = before.state[path];
    const b = after.state[path];
    const wasVisible = a?.visible ?? false;
    const isVisible = b?.visible ?? false;
    if (!wasVisible && isVisible) shown++;
    else if (wasVisible && !isVisible) hidden++;
    else if (a && b && isVisible && stateLabel(a) === 'disabled' && stateLabel(b) !== 'disabled') enabled.push(text(field.label, lang));
    else if (a && b && isVisible && stateLabel(a) !== 'disabled' && stateLabel(b) === 'disabled') disabled.push(text(field.label, lang));
  }
  const parts: string[] = [];
  if (shown) parts.push(t('form.announce.shown', { count: shown }));
  if (hidden) parts.push(t('form.announce.hidden', { count: hidden }));
  for (const label of enabled) parts.push(t('form.announce.enabled', { label }));
  for (const label of disabled) parts.push(t('form.announce.disabled', { label }));
  return parts.join(' ');
}

/** Error-summary items: every error, linked to its field; rows are named ("Riadok 2: …"). */
export function errorSummaryItems(
  formType: string,
  definition: FormDefinition,
  errors: FieldError[],
  t: (key: string, options?: Record<string, unknown>) => string,
  lang: Lang,
): ErrorSummaryItem[] {
  const index = new Map(flatten(definition.fields).map((f) => [f.key, f]));
  const summarizer = new RuleSummarizer(definition);
  return errors.map((e) => {
    const { row, key } = parsePath(e.field);
    const field = index.get(key);
    const message = errorMessage(t as never, field, e, lang, summarizer);
    return { targetId: errorTargetId(formType, field, e.field, e.params), text: row === undefined ? message : t('form.rowError', { n: row + 1, message }) };
  });
}

interface CommonProps {
  formGroupClassName?: string;
  extraDescribedBy?: string;
}

function NumberInput({
  id,
  name,
  field,
  lang,
  labelProps,
  hintProps,
  error,
  required,
  disabled,
  disabledHint,
  value,
  common,
  onCommit,
}: {
  id: string;
  name: string;
  field: FieldDef;
  lang: Lang;
  labelProps: { label: string; labelLang?: string };
  hintProps: { hint?: string; hintLang?: string };
  error?: string;
  required: boolean;
  disabled: boolean;
  disabledHint: ReactNode;
  value: JsonValue | undefined;
  common: CommonProps;
  onCommit: (canonical: string) => void;
}) {
  const formatted = displayNumber(value, lang);
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <Input
      id={id}
      name={name}
      {...labelProps}
      labelClassName="govuk-label--s"
      {...hintProps}
      error={error}
      required={required}
      width={(field.width as never) ?? '10'}
      inputMode="decimal"
      autoComplete="off"
      spellCheck={false}
      value={draft ?? formatted}
      readOnly={disabled}
      aria-disabled={disabled ? true : undefined}
      beforeInput={disabledHint}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== null) onCommit(toCanonicalNumber(draft));
        setDraft(null);
      }}
      {...common}
    />
  );
}

function DateField({
  id,
  field,
  label,
  labelLang,
  hintProps,
  error,
  required,
  disabled,
  disabledHint,
  value,
  common,
  onDraft,
  onCommit,
}: {
  id: string;
  field: FieldDef;
  lang: Lang;
  label: string;
  labelLang?: string;
  hintProps: { hint?: string; hintLang?: string };
  error?: string;
  required: boolean;
  disabled: boolean;
  disabledHint: ReactNode;
  value: JsonValue | undefined;
  common: CommonProps;
  onDraft: (iso: string | undefined) => void;
  onCommit: (iso: string | undefined) => void;
}) {
  const fromValue = typeof value === 'string' ? isoToParts(value) : { day: '', month: '', year: '' };
  const [parts, setParts] = useState<DateParts | null>(null);
  const shown = parts ?? fromValue;
  const toIso = (p: DateParts): string | undefined => {
    if (!p.day.trim() && !p.month.trim() && !p.year.trim()) return undefined;
    return `${p.year.trim()}-${p.month.trim().padStart(2, '0')}-${p.day.trim().padStart(2, '0')}`;
  };
  return (
    <DateInput
      id={id}
      namePrefix={id}
      legend={label}
      legendLang={labelLang}
      legendClassName="govuk-fieldset__legend--s"
      hint={hintProps.hint ?? undefined}
      hintLang={hintProps.hintLang}
      error={error}
      required={required}
      value={shown}
      ariaDisabled={disabled}
      beforeInputs={disabledHint}
      onChange={(p) => {
        if (disabled) return;
        setParts(p);
        onDraft(toIso(p));
      }}
      onBlur={(e) => {
        const group = e.currentTarget.closest('.govuk-date-input');
        // commit when focus leaves the whole date input, not when moving between day/month/year
        if (group && e.relatedTarget instanceof Node && group.contains(e.relatedTarget)) return;
        if (parts) onCommit(toIso(parts));
        setParts(null);
      }}
      formGroupClassName={common.formGroupClassName}
      extraDescribedBy={common.extraDescribedBy}
      key={field.key}
    />
  );
}
